import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import EditProfile from './EditProfile';
import ChangePassword from './ChangePassword';
import ExamHistory from './ExamHistory';
import AffiliatePanel from './AffiliatePanel';
import PaymentPanel from './PaymentPanel';
import Navbar from './Navbar';
import TopPerformerBadge from './TopPerformerBadge';
import { User, Edit, History, ChevronLeft, ChevronRight, BarChart, Headphones, PenTool, ArrowLeft, ArrowRight, KeyRound, Gift, CreditCard } from 'lucide-react';
import { API_BASE } from '../config/api';

const ProfilePage = () => {
  // Add accountStatus state
  const [profileData, setProfileData] = useState(null);
  const [testStats, setTestStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('profile'); // 'profile', 'edit', 'history'
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [statsSection, setStatsSection] = useState(0); // 0 for overview, 1 for detailed stats
  const [imageTimestamp, setImageTimestamp] = useState(Date.now());
  const [accountStatus, setAccountStatus] = useState(null);
  const [vipInfo, setVipInfo] = useState(null);

  // Open a specific tab when arriving via ?tab= (e.g. Affiliate link in navbar).
  useEffect(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    if (tab && ['affiliate', 'payment', 'edit', 'password', 'history'].includes(tab)) {
      setActiveView(tab);
    }
  }, []);

  // Update fetchData to also get account status
  const fetchData = useCallback(async () => {
    try {
      // Fetch profile data
      const profileResponse = await fetch(`${API_BASE}/student/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Fetch test statistics
      const statsResponse = await fetch(`${API_BASE}/student/my-test-statistics`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Fetch account status for students
      if (localStorage.getItem('role') === 'student') {
        const accountResponse = await fetch(`${API_BASE}/account-status`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        if (accountResponse.ok) {
          const accountData = await accountResponse.json();
          setAccountStatus(accountData);
        }
      }

      if (profileResponse.ok) {
        const data = await profileResponse.json();
        setProfileData(data);
        // Update timestamp to force image refresh
        setImageTimestamp(Date.now());
      }

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setTestStats(statsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Add an effect to refresh data when switching back to profile view
  useEffect(() => {
    if (activeView === 'profile') {
      fetchData();
    }
  }, [activeView, fetchData]);

  // Fetch VIP info for customers
  useEffect(() => {
    if (localStorage.getItem('role') === 'customer') {
      fetch(`${API_BASE}/customer/vip/remaining-days`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      })
        .then(res => res.json())
        .then(data => setVipInfo(data));
    }
  }, []);

  const renderMainContent = () => {
    switch (activeView) {
      case 'edit':
        return <EditProfile onProfileUpdate={fetchData} />;
      case 'password':
        return <ChangePassword />;
      case 'history':
        return <ExamHistory />;
      case 'affiliate':
        return <AffiliatePanel onGoPayment={() => setActiveView('payment')} />;
      case 'payment':
        return <PaymentPanel />;
      default:
        return (
          <div className="flex-1">
            <div className="flex items-center gap-4 md:gap-6 mb-8">
              <img
                src={profileData?.image_url ? `${profileData.image_url}?t=${imageTimestamp}` : "/default-avatar.jpg"}
                alt={profileData?.username}
                className="w-16 h-16 md:w-20 md:h-20 rounded-2xl object-cover shrink-0"
                key={imageTimestamp} // Force re-render when timestamp changes
              />
              <div>
                <h1 className="text-2xl font-semibold">{profileData?.username}</h1>
                {profileData?.is_top_performer && (
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    <TopPerformerBadge count={profileData?.read_top10_count} skill="reading" />
                    <TopPerformerBadge count={profileData?.listen_top10_count} skill="listening" />
                  </div>
                )}
                <p className="text-gray-500 text-sm break-all">{profileData?.email}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Đang hoạt động
                  </div>
                  
                  {localStorage.getItem('role') === 'student' && accountStatus && (
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {accountStatus.is_active ? (
                        <span>Thời gian hoạt động còn {accountStatus.remaining_days} ngày</span>
                      ) : accountStatus.activated_at === null ? (
                        <span>Chưa kích hoạt</span>
                      ) : (
                        <span>Đã hết hạn</span>
                      )}
                    </div>
                  )}
                  {/* VIP badge for customer role */}
                  {localStorage.getItem('role') === 'customer' && vipInfo && (
                    <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {vipInfo.has_active_subscription
                        ? <>Gói VIP: còn {vipInfo.remaining_days} ngày ({vipInfo.package_name})</>
                        : <>Chưa có gói VIP</>
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Overview replaced by the dedicated "Tổng Quan Kết Quả" page */}
            <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-[#0096b1]/10 flex items-center justify-center">
                <BarChart className="w-7 h-7 text-[#0096b1]" />
              </div>
              <h2 className="text-xl font-bold text-[#2b5356] mb-2">Tổng Quan Kết Quả</h2>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Xem thống kê chi tiết theo từng kỹ năng: tiến độ, độ chính xác theo dạng câu hỏi và xu hướng cải thiện.
              </p>
              <Link
                to="/results-overview"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#0096b1] text-white font-semibold hover:bg-[#007a90] transition-colors"
              >
                Xem Tổng Quan Kết Quả
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        );
    }
  };

  // Add this function to handle account activation
  const handleActivateAccount = async () => {
    try {
      const response = await fetch(`${API_BASE}/student/activate-account`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAccountStatus(data);
      }
    } catch (error) {
      console.error('Error activating account:', error);
    }
  };

  // Then in the UI, add this below the status badges:
  {localStorage.getItem('role') === 'student' && 
   accountStatus && 
   accountStatus.is_active === 'inactive' && (
    <button
      onClick={handleActivateAccount}
      className="ml-2 text-xs px-2 py-1 bg-[#0096b1] text-white rounded-md hover:bg-[#0096b1] transition-colors"
    >
      Kích hoạt
    </button>
  )}

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-start md:items-center justify-center py-6 md:py-16 px-3 md:px-4">
        <div className="w-full max-w-6xl bg-white rounded-2xl md:rounded-3xl shadow-lg p-4 md:p-6 mx-auto">
          <div className="flex flex-col md:flex-row gap-4 md:gap-6">
            <div className={`w-full ${menuCollapsed ? 'md:w-16' : 'md:w-64'} border-b md:border-b-0 md:border-r border-gray-100 pb-4 md:pb-0 md:pr-6 transition-all duration-300`}>
              <div className="flex items-center justify-center gap-2 mb-8 relative">
                {!menuCollapsed && <span className="font-medium font-bold">Hồ sơ học viên</span>}
                <button
                  onClick={() => setMenuCollapsed(!menuCollapsed)}
                  className="absolute right-0 p-1 hover:bg-gray-100 rounded-full font-bold hidden md:block"
                  aria-label={menuCollapsed ? "Mở rộng menu" : "Thu gọn menu"}
                >
                  {menuCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                </button>
              </div>
              <hr/>
              <nav className="flex md:block gap-2 md:gap-0 overflow-x-auto md:space-y-4 mt-3 pb-1 md:pb-0">
                <div 
                  className={`flex items-center gap-3 p-2 cursor-pointer rounded-lg hover:bg-gray-50 shrink-0 whitespace-nowrap md:shrink ${
                    activeView === 'profile' ? 'text-[#0096b1] bg-[#0096b1]-50' : 'text-gray-600'
                  }`}
                  onClick={() => setActiveView('profile')}
                  title={menuCollapsed ? "Tổng quan hồ sơ" : ""}
                >
                  <User className="w-5 h-5 min-w-5" />
                  {!menuCollapsed && <span>Tổng quan hồ sơ</span>}
                </div>
                <div 
                  className={`flex items-center gap-3 p-2 cursor-pointer rounded-lg hover:bg-gray-50 shrink-0 whitespace-nowrap md:shrink ${
                    activeView === 'edit' ? 'text-[#0096b1] bg-[#0096b1]-50' : 'text-gray-600'
                  }`}
                  onClick={() => setActiveView('edit')}
                  title={menuCollapsed ? "Chỉnh sửa hồ sơ" : ""}
                >
                  <Edit className="w-5 h-5 min-w-5" />
                  {!menuCollapsed && <span>Chỉnh sửa hồ sơ</span>}
                </div>
                <div
                  className={`flex items-center gap-3 p-2 cursor-pointer rounded-lg hover:bg-gray-50 shrink-0 whitespace-nowrap md:shrink ${
                    activeView === 'password' ? 'text-[#0096b1] bg-[#0096b1]-50' : 'text-gray-600'
                  }`}
                  onClick={() => setActiveView('password')}
                  title={menuCollapsed ? "Đổi mật khẩu" : ""}
                >
                  <KeyRound className="w-5 h-5 min-w-5" />
                  {!menuCollapsed && <span>Đổi mật khẩu</span>}
                </div>
                <div
                  className={`flex items-center gap-3 p-2 cursor-pointer rounded-lg hover:bg-gray-50 shrink-0 whitespace-nowrap md:shrink ${
                    activeView === 'affiliate' ? 'text-[#0096b1] bg-[#0096b1]-50' : 'text-gray-600'
                  }`}
                  onClick={() => setActiveView('affiliate')}
                  title={menuCollapsed ? "Affiliate" : ""}
                >
                  <Gift className="w-5 h-5 min-w-5" />
                  {!menuCollapsed && <span>Affiliate</span>}
                  {!menuCollapsed && <span className="ml-auto text-[10px] font-bold text-white bg-[#eb7e37] rounded-full px-1.5 py-0.5">New</span>}
                </div>
                <div
                  className={`flex items-center gap-3 p-2 cursor-pointer rounded-lg hover:bg-gray-50 shrink-0 whitespace-nowrap md:shrink ${
                    activeView === 'payment' ? 'text-[#0096b1] bg-[#0096b1]-50' : 'text-gray-600'
                  }`}
                  onClick={() => setActiveView('payment')}
                  title={menuCollapsed ? "Thanh toán" : ""}
                >
                  <CreditCard className="w-5 h-5 min-w-5" />
                  {!menuCollapsed && <span>Thanh toán</span>}
                  {!menuCollapsed && <span className="ml-auto text-[10px] font-bold text-white bg-[#eb7e37] rounded-full px-1.5 py-0.5">New</span>}
                </div>
              </nav>
            </div>

            {renderMainContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
